import { useEffect, useState, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { Panel, Button } from '../ds/index'
import { PasswordField } from '../components/PasswordField'
import { api, post } from '../lib/api'
import { useInstallBrand } from '../lib/session'
import { HandpickedTree } from '../components/BrandMark'
import { MIN_PASSWORD } from '../lib/password'

type Link = { kind: 'invite' | 'reset'; name: string; email: string; org_name: string; expires_at: string }

/** Where an invite or reset link lands: choose a password, then straight in. */
export default function SetPassword() {
  const { token = '' } = useParams()
  const brand = useInstallBrand()
  const [link, setLink] = useState<Link | null>(null)
  const [gone, setGone] = useState<string | null>(null)
  const [pw, setPw] = useState('')
  const [again, setAgain] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api<Link>(`/api/auth/link/${encodeURIComponent(token)}`)
      .then((l) => { setLink(l); document.title = `${l.org_name} leads` })
      .catch((e: Error) => setGone(e.message))
  }, [token])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setErr(null)
    if (pw !== again) { setErr('The two passwords do not match'); return }
    setBusy(true)
    try {
      await post(`/api/auth/link/${encodeURIComponent(token)}`, { password: pw })
      window.location.assign('/')
    } catch (e2) { setErr((e2 as Error).message); setBusy(false) }
  }

  return (
    <div className="login">
      <Panel className="login-card">
        {brand === 'handpicked' && (
          <div className="hp-login-mark">
            <HandpickedTree size={88} />
            <span className="hp-wordmark">Handpicked</span>
          </div>
        )}
        {gone ? (
          <>
            <div className="login-mark">Link expired</div>
            <p className="login-sub">{gone}</p>
            <a className="btn btn-secondary" href="/">Go to sign in</a>
          </>
        ) : !link || brand === null ? <div aria-busy="true" style={{ minHeight: 200 }} /> : (
          <>
            <div className="login-mark">{link.kind === 'invite' ? `Welcome, ${link.name}` : 'New password'}</div>
            <p className="login-sub">
              {link.kind === 'invite'
                ? `You have a login for ${link.org_name} leads. Choose a password to finish.`
                : `Choose a new password for ${link.email}. Every other device you were signed in on gets signed out.`}
            </p>
            <form onSubmit={submit} className="stack">
              <input type="email" autoComplete="username" value={link.email} readOnly hidden />
              <PasswordField label="Password" autoFocus autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
              <PasswordField label="Password again" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} />
              <p className="form-help" style={{ margin: 0 }}>At least {MIN_PASSWORD} characters. Your sign-in email is {link.email}.</p>
              {err && <p className="form-error" role="alert">{err}</p>}
              <Button type="submit" disabled={busy || pw.length < MIN_PASSWORD || !again}>{busy ? 'Saving' : link.kind === 'invite' ? 'Set password and sign in' : 'Save and sign in'}</Button>
            </form>
          </>
        )}
        <div className="powered">Powered by SPEC Systems</div>
      </Panel>
    </div>
  )
}
